<script setup>
import { get, set, useStorage } from '@vueuse/core'
import Ob from './icons/Ob.vue'
import Arrow from './icons/Arrow.vue'
import { useFutureStore } from '~/store/future'
import { add, div, fixRaw, getPrecision, percent } from '#imports'
import { orderbook, resetOrderbook, subscribe, trades } from '@/apis/socket'
import { useStore } from '~/store'
import { usePanel } from '~/hooks/panel'
import { usePanelStore } from '~/store/panel'

const props = defineProps({
  ob: {
    type: Number,
    default: 0,
  },
})

const { t } = useI18n()
const curStep = ref(null)
const store = useStore()
const future = useFutureStore()
const panel = usePanelStore()
const obKey = useStorage('future_obkey', 0)
const { unitName } = usePanel()

const obs = computed(() => [
  { buy: true, sell: true, key: 0 },
  { buy: true, key: 1 },
  { sell: true, key: -1 },
])

const steps = computed(() => future.steps?.map(i => ({
  label: Number.isNaN(+i) ? i : fix(i, getPrecision(i)),
  value: i,
})))

const maxLength = computed(() => props.ob ? 16 : 24)
const rateClass = computed(() => ({ 'text-rise': future.overview?.r > 0, 'text-fall': future.overview?.r < 0 }))
const priceText = computed(() => fix(future.overview?.c, future.curInfo.ps))
const markPriceText = computed(() => fix(future.overview?.mark, future.curInfo.ps))
const openOrdersPriceList = computed(() => (future.openOrdersByCode[future.code] || []).filter(i => i.lm).map(i => i.price))

function formatData({ arr = [], maxlen = maxLength.value, isAsks, code }) {
  if (maxlen === 0 || code !== future.code)
    arr = []

  const res = arr.reduce((sofar, cur, idx) => {
    const [price, amount] = cur
    const a = future.getRealAmount(amount) || 0
    sofar.max = Math.max(sofar.max, a)
    sofar.t = add(sofar.t, a)
    sofar.ta = add(sofar.ta, amount)
    sofar.ta2usdt = mul(amount, price).mul(future.size).add(sofar.ta2usdt)
    const ta2coin = mul(sofar.ta, future.size)
    const ap = div(sofar.ta2usdt, ta2coin)
    if (idx < maxlen)
      sofar.total = add(sofar.total, a)

    sofar.list[idx] = {
      id: price,
      p: price,
      a,
      t: sofar.t,
      ta: sofar.ta,
      empty: false,
      ap,
      amount,
      markPoint: openOrdersPriceList.value.includes(price),
      ta2coin,
      ta2usdt: sofar.ta2usdt,
    }
    return sofar
  }, {
    list: Array.from({ length: maxLength.value }).fill('').map((_, j) => ({ id: `idx_${j}`, p: '---', a: '---', t: '---', amount: 0, empty: true })),
    max: 0,
    t: 0,
    ta: 0,
    total: 0,
    ta2usdt: 0,
  })
  res.list = res.list.slice(0, maxlen).reverse()

  const first = res.list.slice(-1)
  if (first.length) {
    const [{ p, empty }] = first
    if (!empty) {
      if (isAsks)
        future.bbo.a1 = p
      else
        future.bbo.b1 = p

      future.bbo.code = get(orderbook).code
    }
  }
  return res
}

const map = computed(() => ({
  asks: {
    '0': maxLength.value / 2,
    '1': 0,
    '-1': maxLength.value,
  },
  bids: {
    '0': maxLength.value / 2,
    '1': maxLength.value,
    '-1': 0,
  },
}))

const asks = computed(() => {
  const o = get(orderbook)
  if (o)
    return formatData({ arr: o.asks, maxlen: map.value.asks[get(obKey)], code: store.getCodeById(o.contractId), isAsks: true })
  return {}
})

const bids = computed(() => {
  const o = get(orderbook)

  if (o)
    return formatData({ arr: o.bids, maxlen: map.value.bids[get(obKey)], code: store.getCodeById(o.contractId) })
  return {}
})

watch(steps, (nv) => {
  if (nv) {
    set(curStep, nv[0].value)
    getData(nv[0].value)
  }
}, {
  immediate: true,
})

function getData(step) {
  resetOrderbook()
  subscribe({ topic: 'orderbook', step, contractId: future.id, reborn: true })
  future.bbo = {}
}

const arrow = computed(() => {
  const nv = +trades.value[0]?.price
  const ov = +trades.value[1]?.price
  return nv > ov ? 1 : nv < ov ? -1 : 0
})

function setPanelPrice(value) {
  if (Number.isNaN(+value))
    return

  const p = fixRaw(value, future.ps)
  if (panel.isTrigger)
    panel.stop.triggerPrice = p

  if (panel.isLimit)
    panel.price = p
}
</script>

<template>
  <div>
    <div class="flex items-center justify-between h-[30px] px-3">
      <div class="flex flex-1 gap-1">
        <Ob v-for="i in obs" :key="i.key" v-bind="i" :active="i.key === +obKey" @click="obKey = i.key" />
      </div>
      <div>
        <common-x-n-select v-model:value="curStep" :options="steps" size="tiny" :show-checkmark="false" :consistent-menu-width="false" @update:value="getData" />
      </div>
    </div>
    <div>
      <div class="w-full h-full text-end space-y-0.5 text-[10px] font-medium">
        <div class="flex items-start h-5 px-3 text-textSecondary leading-[10px] text-[10px]">
          <span class="flex-1 text-start">{{ $t('价格') }}<template v-if="future.quote">({{ future.quote }})</template></span>
          <span class="flex-1 px-1">{{ $t('数量') }}({{ unitName }})</span>
          <span class="flex-1">{{ $t('累计数量') }}({{ unitName }})</span>
        </div>
        <div class="flex flex-col">
          <dl>
            <common-x-tips-simple
              v-for="(i) in asks.list"
              :key="i.id"
              placement="left"
              :disabled="!+i.a"
              content-class="!px-2 !py-1"
            >
              <template #content>
                <ul class="min-w-[200px] text-sm text-textPrimary space-y-1">
                  <li class="flex justify-between">
                    <span>{{ $t('均价') }}</span>
                    <span>{{ future.toHuman(i.ap, future.ps, { usingGroup: true }) }}</span>
                  </li>
                  <li class="flex justify-between">
                    <span>{{ $t('累计{0}', [future.base]) }}</span>
                    <span>{{ future.toHuman(i.ta2coin, future.scale, { usingGroup: true }) }}</span>
                  </li>
                  <li class="flex justify-between">
                    <span>{{ $t('累计{0}', [future.quote]) }}</span>
                    <span>{{ future.toHuman(i.ta2usdt, 2, { usingGroup: true }) }}</span>
                  </li>
                </ul>
              </template>
              <dd
                class="relative px-3 flex w-full py-px h-4 leading-4 cursor-pointer select-none hover:bg-inputHovered border-dashed border-t-[0.5px] border-transparent hover:border-tooltip [&:hover~dd]:bg-inputHovered"
                @click="setPanelPrice(i.p)"
              >
                <div v-if="i.markPoint" class="absolute w-1 h-1 -translate-y-1/2 rounded-full bg-sell left-1 top-1/2" />
                <b class="absolute top-0 -right-0 w-[240px] h-full duration-[300ms] transition-transform origin-right scale-0 bg-sell opacity-[0.15]" :style="{ transform: `scaleX(${asks.total ? ((i.t || 0) / asks.total).toFixed(2) : 0})` }" />
                <span class="relative flex-1 text-start text-sell">{{ fix(i.p, future.ps) }}</span>
                <span class="relative flex-1 px-1">{{ future.toHuman(i.a, future.scale, { usingGroup: true }) }}</span>
                <span class="relative flex-1">{{ future.toHuman(i.t, future.scale, { usingGroup: true }) }}</span>
              </dd>
            </common-x-tips-simple>
          </dl>
          <div class="flex items-center justify-between w-full px-3 font-medium h-9" :class="{ '-mb-0.5': obKey }">
            <em class="inline-flex items-center text-lg" :class="rateClass">
              <span class="text-base" :class="{ 'text-green3': arrow > 0, 'text-red3': arrow < 0 }">{{ priceText }}</span>
              <Arrow :arrow="arrow" />
            </em>
            <em class="text-xs">{{ markPriceText }}</em>
          </div>
          <dl class="flex flex-col-reverse">
            <common-x-tips-simple
              v-for="(i) in bids.list"
              :key="i.id"
              placement="left"
              :disabled="!+i.a"
              content-class="!px-2 !py-1"
            >
              <template #content>
                <ul class="min-w-[200px] text-sm text-textPrimary space-y-1">
                  <li class="flex justify-between">
                    <span>{{ $t('均价') }}</span>
                    <span>{{ future.toHuman(i.ap, future.ps, { usingGroup: true }) }}</span>
                  </li>
                  <li class="flex justify-between">
                    <span>{{ $t('累计{0}', [future.base]) }}</span>
                    <span>{{ future.toHuman(i.ta2coin, future.scale, { usingGroup: true }) }}</span>
                  </li>
                  <li class="flex justify-between">
                    <span>{{ $t('累计{0}', [future.quote]) }}</span>
                    <span>{{ future.toHuman(i.ta2usdt, 2, { usingGroup: true }) }}</span>
                  </li>
                </ul>
              </template>
              <dd
                class="relative px-3 flex w-full py-px h-4 leading-4 cursor-pointer select-none hover:bg-inputHovered border-dashed border-b-[0.5px] border-transparent hover:border-tooltip [&:hover~dd]:bg-inputHovered"
                @click="setPanelPrice(i.p)"
              >
                <div v-if="i.markPoint" class="absolute w-1 h-1 -translate-y-1/2 rounded-full bg-buy left-1 top-1/2" />
                <b class="absolute top-0 -right-0 w-[240px] h-full duration-[300ms] transition-transform origin-right scale-0 bg-buy opacity-[0.15]" :style="{ transform: `scaleX(${bids.total ? ((i.t || 0) / bids.total).toFixed(2) : 0})` }" />
                <span class="relative flex-1 text-start text-buy">{{ fix(i.p, future.ps) }}</span>
                <span class="relative flex-1 px-1">{{ future.toHuman(i.a, future.scale, { usingGroup: true }) }}</span>
                <span class="relative flex-1">{{ future.toHuman(i.t, future.scale, { usingGroup: true }) }}</span>
              </dd>
            </common-x-tips-simple>
          </dl>
        </div>
      </div>
    </div>
  </div>
</template>
